import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Card, Form, Input, Modal, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { AxiosResponse } from "axios";
import { formatterPhone } from "@zhihaoscm/utils";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import {
    DateRangePicker as ZhDateRangePicker,
    Dialog as ZhDialog,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import { dateTimeFormat, getPublicFileUrl } from "@/utils";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { fmtNum } from "@/views/pages/tradeManage/activityManagement/activityOrder/tool";
import { ActivityOrderPreviewPhone } from "@/views/pages/tradeManage/activityManagement/activityOrder/components/previewPhone";
import { useUserStore } from "@/views/stores";
import type {
    ActivityOrderPageResult,
    ActivityOrderExportQuery,
    ActivityOrderRow,
    ActivityOrderSpecial,
    ActivityQuery
} from "./types";

// 活动订单沿用 Vue 分页默认值，首屏和筛选请求统一返回 10 条。
const defaultQuery: ActivityQuery = { page: 1, size: 10 };
type ActivityOrderFilterValues = ActivityQuery & { payTime?: [string, string] | null };

/** 将 Vue daterange 的日期值转换为活动订单接口要求的整日时间边界。 */
function formatActivityOrderDateBoundary(value: string, endOfDay: boolean): string {
    return `${value} ${endOfDay ? "23:59:59" : "00:00:00"}`;
}

export interface ActivityOrderPageProps {
    assignOrder?: (orderId: number | string, handlerId: number | string) => Promise<void>;
    exportOrders?: (query: ActivityOrderExportQuery) => Promise<void>;
    loadSpecials?: () => Promise<ActivityOrderSpecial[]>;
    loadFiles?: (ids: number[]) => Promise<Array<{ id?: number; path?: string }>>;
    loadOrders?: (query: ActivityQuery) => Promise<ActivityOrderPageResult>;
    permissions?: string[];
}

/** 复用 Vue 活动订单分页接口。 */
export async function loadActivityOrders(query: ActivityQuery): Promise<ActivityOrderPageResult> {
    const response = await request.service.get<ActivityOrderPageResult>("/admin/order/activity/paging", {
        params: query
    });
    return response.data;
}

/** 生成 Vue 活动订单导出时使用的本地时间文件名。 */
function getActivityOrderExportFilename(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `活动订单-${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}.xlsx`;
}

/** 将活动订单 Blob 转换为浏览器下载，并清理临时锚点和 Object URL。 */
function downloadActivityOrderBlob(blob: Blob, filename: string): void {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);

    try {
        link.click();
    } finally {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    }
}

/** 复用 Vue 活动订单导出接口并触发浏览器文件下载。 */
export async function exportActivityOrders(query: ActivityOrderExportQuery): Promise<void> {
    const response = await request.service.get<Blob, AxiosResponse<Blob>>("/admin/order/exportActivityOrder", {
        params: query,
        responseType: "blob",
        returnRawResponse: true,
        timeout: 60000
    } as CustomAxiosRequestConfig);
    downloadActivityOrderBlob(response.data, getActivityOrderExportFilename(new Date()));
}

/** 旧 Vue PreviewPhone 直接读取当前行 productInfo，并按文件 ID 补全媒体地址。 */
export async function loadActivityProductFiles(ids: number[]): Promise<Array<{ id?: number; path?: string }>> {
    const response = await request.service.post<Array<{ id?: number; path?: string }>>(
        "/dataService/admin/file/ids",
        ids
    );
    return response.data;
}

/** 复用 Vue 活动订单专员列表接口。 */
export async function loadActivityOrderSpecials(): Promise<ActivityOrderSpecial[]> {
    const response = await request.service.get<ActivityOrderSpecial[]>("/admin/order/find/activity-specials");
    return response.data;
}

/** 复用 Vue 活动订单指派接口。 */
export async function assignActivityOrder(orderId: number | string, handlerId: number | string): Promise<void> {
    await request.service.put(`/admin/order/assign/${orderId}/${handlerId}`);
}

/** 活动订单列表，迁移 Vue 核心订单字段和导出权限入口。 */
export function ActivityOrderPage({
    assignOrder = assignActivityOrder,
    exportOrders = exportActivityOrders,
    loadSpecials = loadActivityOrderSpecials,
    loadFiles = loadActivityProductFiles,
    loadOrders = loadActivityOrders,
    permissions
}: ActivityOrderPageProps) {
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canWrite = currentPermissions.includes("ROLE_ACTIORDER_W");
    const [items, setItems] = useState<ActivityOrderRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [query, setQuery] = useState<ActivityQuery>(defaultQuery);
    const [previewProduct, setPreviewProduct] = useState<ActivityOrderRow["productInfo"]>();
    const [previewFiles, setPreviewFiles] = useState<Record<number, string>>({});
    const [previewTab, setPreviewTab] = useState<"video" | "image">("video");
    const [assignTarget, setAssignTarget] = useState<ActivityOrderRow | null>(null);
    const [specials, setSpecials] = useState<ActivityOrderSpecial[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [specialKeyword, setSpecialKeyword] = useState("");
    const [selectedOrderIds, setSelectedOrderIds] = useState<Array<number | string>>([]);
    const [filterForm] = Form.useForm<ActivityOrderFilterValues>();

    useEffect(() => {
        let disposed = false;
        void loadOrders(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(result.list || []);
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("活动订单加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [loadOrders, query]);

    const submitFilters = (values: ActivityOrderFilterValues) => {
        const { payTime, ...rawFilters } = values;
        const [payStartTimeValue, payEndTimeValue] = payTime || [];
        // 支付结束日期不能早于开始日期，避免向 Vue 分页接口发送反向时间范围。
        const payStartTime = Date.parse(String(payStartTimeValue ?? ""));
        const payEndTime = Date.parse(String(payEndTimeValue ?? ""));
        if (Number.isFinite(payStartTime) && Number.isFinite(payEndTime) && payEndTime < payStartTime) {
            setErrorMessage("支付结束日期不能早于开始日期");
            return;
        }
        const filters = Object.fromEntries(
            Object.entries(rawFilters).filter(([, value]) => value !== undefined && value !== null && value !== "")
        ) as Omit<ActivityQuery, "page" | "size">;
        if (payStartTimeValue) filters.payStartTime = formatActivityOrderDateBoundary(payStartTimeValue, false);
        if (payEndTimeValue) filters.payEndTime = formatActivityOrderDateBoundary(payEndTimeValue, true);
        setErrorMessage(null);
        setQuery(current => ({ page: 1, size: current.size, ...filters }));
    };

    const resetFilters = () => {
        filterForm.resetFields();
        setQuery(current => ({ page: 1, size: current.size }));
    };

    const showSnapshot = async (productInfo?: ActivityOrderRow["productInfo"]) => {
        if (!productInfo) return;
        setPreviewProduct(productInfo);
        setPreviewTab("video");
        const ids = [productInfo.videoId, ...(productInfo.imgIds || [])].filter(
            (id): id is number => typeof id === "number" && Number.isFinite(id)
        );
        if (!ids.length) {
            setPreviewFiles({});
            return;
        }
        try {
            const files = await loadFiles(ids);
            setPreviewFiles(
                Object.fromEntries(files.filter(file => file.id != null && file.path).map(file => [file.id, file.path]))
            );
        } catch {
            setErrorMessage("交易快照加载失败");
        }
    };

    const showAssign = async (row: ActivityOrderRow) => {
        if (row.id == null) return;
        try {
            setSpecials(await loadSpecials());
            setSpecialKeyword("");
            setSelectedHandlerId(row.handlerId == null ? "" : String(row.handlerId));
            setAssignTarget(row);
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    const submitAssign = async () => {
        if (assignTarget?.id == null || !selectedHandlerId) return;
        try {
            await assignOrder(assignTarget.id, selectedHandlerId);
            setAssignTarget(null);
            setQuery(current => ({ ...current }));
        } catch {
            setErrorMessage("活动订单指派失败");
        }
    };

    const submitExport = async () => {
        try {
            // 沿用 Vue 导出规则：有选中订单时优先提交订单 ID，否则提交当前筛选条件。
            const { page: _page, size: _size, ...filters } = query;
            void _page;
            void _size;
            await exportOrders(selectedOrderIds.length ? { ids: selectedOrderIds } : filters);
        } catch {
            // 导出接口失败时保留当前订单列表，并反馈页面级错误。
            setErrorMessage("活动订单导出失败");
        }
    };

    // 将 zhihao Table 的支付时间排序转换为 Vue 分页接口的字段和值。
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => {
            const nextQuery = { ...current, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (prop === "payTime" && order) {
                nextQuery.sortKey = "pay_time";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 活动订单表格字段和行级操作插槽。
    const orderColumns: TableColumnProps<ActivityOrderRow>[] = [
        ...(canWrite
            ? ([
                  { key: "selection", type: "selection", label: "选择", prop: "id" }
              ] satisfies TableColumnProps<ActivityOrderRow>[])
            : []),
        { key: "id", label: "订单编号", width: 160, prop: "id", render: ({ row }) => row.id ?? "-" },
        {
            key: "supplierName",
            label: "供应商",
            minWidth: 190,
            prop: "supplierName",
            render: ({ row }) => row.productInfo?.supplierEnterprise?.name || row.supplierName || "-"
        },
        {
            key: "productTitle",
            label: "活动商品",
            minWidth: 160,
            prop: "id",
            render: ({ row }) => row.productInfo?.title || "-"
        },
        {
            key: "customer",
            label: "采购用户",
            minWidth: 240,
            prop: "id",
            render: ({ row }) => {
                const customerName = row.customerEnterprise?.name || row.customerEnterprise?.realName || "-";
                const mobile = row.customerEnterprise?.mobile;
                const customerLabel = `${customerName} | ${mobile ? formatterPhone(mobile) : "-"}`;
                // 对齐旧 Vue click：有客户编号时跳转联运用户详情，缺编号时保留原文本展示。
                return row.customerId == null ? (
                    customerLabel
                ) : (
                    <Link
                        aria-label={`${customerName} | ${mobile || "-"}`}
                        to={`/lianYunUser/userInfo?userId=${row.customerId}`}
                    >
                        {customerLabel}
                    </Link>
                );
            }
        },
        {
            key: "payAmount",
            label: "意向金(元)",
            width: 130,
            prop: "payAmount",
            align: "right",
            headerAlign: "right",
            render: ({ row }) => fmtNum(row.payAmount)
        },
        {
            key: "handlerName",
            label: "订单专员",
            width: 90,
            prop: "handlerName",
            render: ({ row }) => row.handlerName || "-"
        },
        {
            key: "payTime",
            label: "支付时间",
            prop: "payTime",
            sortable: "custom",
            width: 170,
            render: ({ row }) => dateTimeFormat(row.payTime)
        },
        { key: "remark", label: "用户备注", width: 230, prop: "remark", render: ({ row }) => row.remark || "-" },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            width: 120,
            fixed: "right",
            render: ({ row }) =>
                currentPermissions.includes("ROLE_ACTIORDER_R") || canWrite ? (
                    <Space>
                        {canWrite ? (
                            <Button
                                aria-label={`指派订单${row.id || ""}`}
                                type="link"
                                onClick={() => void showAssign(row)}
                            >
                                {row.handlerName ? "变更指派" : "指派"}
                            </Button>
                        ) : null}
                        {currentPermissions.includes("ROLE_ACTIORDER_R") ? (
                            <Button
                                aria-label={`查看订单快照${row.id || ""}`}
                                type="link"
                                disabled={banButton}
                                onClick={() => void showSnapshot(row.productInfo)}
                            >
                                交易快照
                            </Button>
                        ) : null}
                    </Space>
                ) : null
        }
    ];

    return (
        <section aria-label="activity-order-page" className="flex h-full min-h-full flex-col">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<ActivityOrderRow>
                    className="flex-1"
                    data={items}
                    columns={normalizeOperationColumns(orderColumns, items)}
                    onSortChange={handleSortChange}
                    onSelectionChange={
                        canWrite
                            ? rows =>
                                  setSelectedOrderIds(
                                      rows
                                          .map(row => row.id)
                                          .filter((id): id is number | string => id !== undefined && id !== null)
                                  )
                            : undefined
                    }
                    pagination={false}
                    rowKey={row => String(row.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                活动订单
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canWrite ? (
                                    <Button
                                        aria-label="导出活动订单"
                                        className="admin-table-primary-action"
                                        type="primary"
                                        onClick={() => void submitExport()}
                                    >
                                        导出
                                    </Button>
                                ) : null}
                                <Form.Item label="订单编号" name="id">
                                    <Input placeholder="请输入订单编号搜索" />
                                </Form.Item>
                                <Form.Item label="供应商" name="supplierKeyword">
                                    <Input placeholder="请输入供应商搜索" />
                                </Form.Item>
                                <Form.Item label="活动商品" name="activityProduct">
                                    <Input aria-label="活动商品" placeholder="请输入活动商品搜索" />
                                </Form.Item>
                                <Form.Item label="采购用户" name="keyword">
                                    <Input placeholder="请输入采购用户搜索" />
                                </Form.Item>
                                <Form.Item label="订单专员" name="handlerName">
                                    <Input placeholder="请输入订单专员搜索" />
                                </Form.Item>
                                <Form.Item label="支付时间" name="payTime">
                                    <ZhDateRangePicker
                                        format="YYYY-MM-DD"
                                        rangeSeparator="-"
                                        startPlaceholder="支付开始日期"
                                        endPlaceholder="支付结束日期"
                                        type="daterange"
                                        valueFormat="YYYY-MM-DD"
                                    />
                                </Form.Item>
                                <Button
                                    aria-label="查询活动订单"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置活动订单筛选"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 订单分页契约，切换页大小后回到第 1 页并保留筛选条件。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
                <Modal
                    centered
                    className="activity-order-snapshot-modal"
                    destroyOnHidden
                    footer={null}
                    maskTransitionName=""
                    open={Boolean(previewProduct)}
                    title="交易快照"
                    transitionName=""
                    onCancel={() => setPreviewProduct(undefined)}
                >
                    {previewProduct ? (
                        <div className="activity-order-phone-preview">
                            <div className="activity-order-phone-preview__panel">
                                <div className="activity-order-phone-preview__page">
                                    <div className="activity-order-phone-preview__content">
                                        {/* 交易快照组件继续消费 Vue productInfo 的 campaignUnitPrice、campaignRule、videoId 和 imgIds。 */}
                                        <ActivityOrderPreviewPhone
                                            files={previewFiles}
                                            info={previewProduct}
                                            tab={previewTab}
                                            onTabChange={setPreviewTab}
                                        />
                                    </div>
                                </div>
                                <img
                                    alt=""
                                    className="activity-order-phone-preview__border"
                                    src={getPublicFileUrl("admin/iPhone-border.png")}
                                    width={439}
                                />
                            </div>
                        </div>
                    ) : null}
                </Modal>
                <ZhDialog
                    open={assignTarget !== null}
                    modalClass="assign-dialog-modal"
                    title="指派专员"
                    width="842px"
                    onCancel={() => setAssignTarget(null)}
                    footer={
                        <Space>
                            <Button onClick={() => setAssignTarget(null)}>取消</Button>
                            <Button
                                aria-label="确认指派"
                                disabled={!selectedHandlerId}
                                type="primary"
                                onClick={() => void submitAssign()}
                            >
                                确定
                            </Button>
                        </Space>
                    }
                >
                    <div className="assign-dialog-body">
                        <Input
                            aria-label="专员姓名"
                            className="assign-dialog-search"
                            placeholder="请输入姓名"
                            suffix={<TableSearchIcon name="search" />}
                            value={specialKeyword}
                            onChange={event => setSpecialKeyword(event.target.value)}
                        />
                        {specials.filter(special => (special.user.name || "").includes(specialKeyword.trim()))
                            .length ? (
                            <div className="assign-dialog-list">
                                {specials
                                    .filter(special => (special.user.name || "").includes(specialKeyword.trim()))
                                    .map(special => {
                                        const selected = String(special.user.id) === selectedHandlerId;
                                        return (
                                            <button
                                                aria-label={`选择${special.user.name || "订单专员"}`}
                                                aria-pressed={selected}
                                                className={`assign-dialog-card${selected ? " is-selected" : ""}`}
                                                key={String(special.user.id)}
                                                type="button"
                                                onClick={() => setSelectedHandlerId(String(special.user.id))}
                                            >
                                                <div className="assign-dialog-card-info">
                                                    <div className="assign-dialog-card-name">
                                                        {special.user.name || "-"}{" "}
                                                        <span className="assign-dialog-employee-id">
                                                            {special.user.employeeId || ""}
                                                        </span>
                                                    </div>
                                                    <div className="assign-dialog-follow-up">
                                                        跟进的订单数量 <b>{special.orderCount ?? ""}</b> 条
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                            </div>
                        ) : (
                            <div className="py-12 text-center text-gray-400">暂未找到可指派的订单专员</div>
                        )}
                    </div>
                </ZhDialog>
            </Card>
        </section>
    );
}

export default ActivityOrderPage;
